/**
 * SparkBalanceWidget - Subtle spark balance display in lower left corner
 * Shows current balance, link to upgrade, and reload date for paid plans
 * Warns when balance drops below 1,000 sparks
 * Can be collapsed to a small icon on the left edge
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Zap, Plus, Calendar, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { getApiBaseUrl } from "@/config/api";
import { toast } from "sonner";

const API = getApiBaseUrl();
const LOW_BALANCE_THRESHOLD = 1000;

export default function SparkBalanceWidget() {
  const navigate = useNavigate();
  const { user, token, isActor } = useAuth();
  const [sparkData, setSparkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Persist collapse state in localStorage
    return localStorage.getItem('sparkWidgetCollapsed') === 'true';
  });

  useEffect(() => {
    if (!token || isActor) {
      setLoading(false);
      return;
    }
    fetchSparkData();
  }, [token, isActor]);

  // Save collapse state to localStorage
  useEffect(() => {
    localStorage.setItem('sparkWidgetCollapsed', isCollapsed);
  }, [isCollapsed]);

  // Show low balance warning toast (once per session)
  useEffect(() => {
    if (sparkData && !hasShownWarning && sparkData.spark_balance < LOW_BALANCE_THRESHOLD) {
      setHasShownWarning(true);
      toast.warning(
        `Low Spark Balance: ${sparkData.spark_balance.toLocaleString()} sparks remaining`,
        {
          description: "Consider adding more sparks to avoid interruptions.",
          action: {
            label: "Spark-Up",
            onClick: () => navigate("/account?tab=billing")
          },
          duration: 8000
        }
      );
    }
  }, [sparkData, hasShownWarning, navigate]);

  const fetchSparkData = async () => {
    try {
      const response = await axios.get(`${API}/api/billing/my-sparks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSparkData(response.data);
    } catch (error) {
      console.error("Failed to fetch spark data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Don't show for actors or when not logged in
  if (!token || isActor || loading) {
    return null;
  }

  // Format the reload date nicely
  const formatReloadDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const isFreePlan = sparkData?.subscription_plan === 'ambassador';
  const reloadDate = sparkData?.subscription_end_date ? formatReloadDate(sparkData.subscription_end_date) : null;
  const monthlySparks = sparkData?.monthly_sparks || 0;
  const isLowBalance = sparkData?.spark_balance < LOW_BALANCE_THRESHOLD;

  // Collapsed view - minimal icon on left edge
  if (isCollapsed) {
    return (
      <div 
        className="fixed bottom-4 left-0 z-40 animate-fadeIn"
        data-testid="spark-balance-widget-collapsed"
      >
        <button
          onClick={() => setIsCollapsed(false)}
          className={`flex items-center justify-center w-8 h-10 rounded-r-lg shadow-lg transition-all hover:w-10 ${
            isLowBalance 
              ? 'bg-amber-500/20 border border-l-0 border-amber-500/50 hover:bg-amber-500/30' 
              : 'bg-zinc-900/90 border border-l-0 border-zinc-700 hover:bg-zinc-800'
          }`}
          title={`${sparkData?.spark_balance?.toLocaleString() || 0} sparks - Click to expand`}
        >
          <div className="flex items-center gap-0.5">
            {isLowBalance ? (
              <AlertTriangle className="w-4 h-4 text-amber-400" />
            ) : (
              <Zap className="w-4 h-4 text-indigo-400" />
            )}
            <ChevronRight className="w-3 h-3 text-zinc-500" />
          </div>
        </button>
      </div>
    );
  }

  // Expanded view - full widget
  return (
    <div 
      className="fixed bottom-4 left-4 z-40 animate-fadeIn"
      data-testid="spark-balance-widget"
    >
      <div 
        className={`bg-zinc-900/90 backdrop-blur-sm border rounded-lg px-3 py-2 shadow-lg hover:border-zinc-700 transition-colors cursor-pointer group ${
          isLowBalance ? 'border-amber-500/50' : 'border-zinc-800'
        }`}
        onClick={() => navigate("/account?tab=billing")}
      >
        <div className="flex items-center gap-3">
          {/* Collapse Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsCollapsed(true);
            }}
            className="flex items-center justify-center w-5 h-5 rounded hover:bg-zinc-700/50 transition-colors"
            title="Collapse widget"
          >
            <ChevronLeft className="w-4 h-4 text-zinc-500 hover:text-zinc-300" />
          </button>

          {/* Spark Icon & Balance */}
          <div className="flex items-center gap-1.5">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              isLowBalance ? 'bg-amber-500/20' : 'bg-indigo-500/20'
            }`}>
              {isLowBalance ? (
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              ) : (
                <Zap className="w-3.5 h-3.5 text-indigo-400" />
              )}
            </div>
            <span className={`font-semibold text-sm ${isLowBalance ? 'text-amber-400' : 'text-white'}`} data-testid="widget-spark-balance">
              {sparkData?.spark_balance?.toLocaleString() || 0}
            </span>
          </div>

          {/* Divider */}
          <div className="w-px h-5 bg-zinc-700" />

          {/* Spark Up Link */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate("/account?tab=billing");
            }}
            className={`flex items-center gap-1 text-xs transition-colors ${
              isLowBalance 
                ? 'text-amber-400 hover:text-amber-300' 
                : 'text-indigo-400 hover:text-indigo-300'
            }`}
            data-testid="spark-up-link"
          >
            <Plus className="w-3 h-3" />
            <span>Spark-Up</span>
          </button>
        </div>

        {/* Low Balance Warning */}
        {isLowBalance && (
          <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-amber-500/30">
            <span className="text-[10px] text-amber-400/80">
              Low balance - add sparks soon
            </span>
          </div>
        )}

        {/* Reload Date - Only for paid plans (when not showing low balance warning) */}
        {!isLowBalance && !isFreePlan && reloadDate && monthlySparks > 0 && (
          <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-zinc-800">
            <Calendar className="w-3 h-3 text-zinc-500" />
            <span className="text-[10px] text-zinc-500">
              +{monthlySparks.toLocaleString()} on {reloadDate}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
